import { motion } from "framer-motion";
import { EASE } from "@/lib/motion";

const draw = (delay = 0) => ({
    initial: { pathLength: 0, opacity: 0 },
    whileInView: { pathLength: 1, opacity: 1 },
    viewport: { once: true },
    transition: { duration: 1.8, delay, ease: EASE },
});

/** A distributed plan: independent nodes becoming one legible system. */
export function ConstellationsGraphic({ className = "" }) {
    const points = [
        [58, 91], [128, 42], [190, 78], [260, 34],
        [332, 74], [406, 46], [482, 94], [278, 142], [382, 156],
    ];
    const edges = [
        [0, 1], [0, 2], [1, 2], [1, 3], [2, 3], [2, 7],
        [3, 4], [3, 6], [4, 5], [4, 6], [4, 7], [5, 6],
        [6, 8], [7, 8],
    ];

    return (
        <svg viewBox="0 0 540 200" className={className} aria-hidden="true">
            {edges.map(([a, b], i) => (
                <motion.line
                    key={`${a}-${b}`}
                    x1={points[a][0]}
                    y1={points[a][1]}
                    x2={points[b][0]}
                    y2={points[b][1]}
                    stroke="currentColor"
                    strokeWidth="1"
                    opacity="0.2"
                    {...draw(i * 0.035)}
                />
            ))}
            {points.map(([cx, cy], i) => (
                <motion.g
                    key={`${cx}-${cy}`}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.14 + i * 0.055, ease: EASE }}
                    style={{ transformOrigin: `${cx}px ${cy}px` }}
                >
                    <circle cx={cx} cy={cy} r={i === 3 || i === 7 ? 7 : 4} fill="currentColor" opacity={i === 3 || i === 7 ? 0.75 : 0.48} />
                    {(i === 3 || i === 7) && (
                        <circle cx={cx} cy={cy} r="15" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.16" />
                    )}
                </motion.g>
            ))}
        </svg>
    );
}

/** A quiet room: nested thresholds drawing information toward a clear centre. */
export function AtriumGraphic({ className = "" }) {
    const frames = [
        { x: 72, y: 24, w: 396, h: 152, o: 0.12 },
        { x: 116, y: 42, w: 308, h: 134, o: 0.18 },
        { x: 160, y: 60, w: 220, h: 116, o: 0.25 },
        { x: 204, y: 78, w: 132, h: 98, o: 0.34 },
    ];

    return (
        <svg viewBox="0 0 540 200" className={className} aria-hidden="true">
            {frames.map((f, i) => (
                <motion.path
                    key={f.x}
                    d={`M ${f.x} 176 V ${f.y} H ${f.x + f.w} V 176`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={i === frames.length - 1 ? 1.5 : 1}
                    opacity={f.o}
                    {...draw(i * 0.12)}
                />
            ))}
            {[72, 116, 160, 204, 270, 336, 380, 424, 468].map((x, i) => (
                <motion.line
                    key={x}
                    x1="270"
                    y1="176"
                    x2={x}
                    y2="194"
                    stroke="currentColor"
                    strokeWidth="1"
                    opacity="0.1"
                    {...draw(0.35 + i * 0.025)}
                />
            ))}
            <motion.circle
                cx="270"
                cy="126"
                r="5"
                fill="currentColor"
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 0.72 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.72, ease: EASE }}
            />
            <motion.circle
                cx="270"
                cy="126"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                opacity="0.16"
                {...draw(0.58)}
            />
        </svg>
    );
}

/** Editorial output: a body of text made clearer by annotations at its edge. */
export function MarginaliaGraphic({ className = "" }) {
    const textLines = [
        [74, 54, 244], [74, 76, 198], [74, 98, 230],
        [74, 120, 174], [74, 142, 218],
    ];
    const notes = [
        { y: 64, width: 92, anchor: 244 },
        { y: 126, width: 116, anchor: 218 },
    ];

    return (
        <svg viewBox="0 0 540 200" className={className} aria-hidden="true">
            <motion.line
                x1="340" y1="34" x2="340" y2="166"
                stroke="currentColor" strokeWidth="1" opacity="0.16"
                {...draw()}
            />
            {textLines.map(([x, y, width], i) => (
                <motion.line
                    key={y}
                    x1={x}
                    y1={y}
                    x2={x + width}
                    y2={y}
                    stroke="currentColor"
                    strokeWidth={i === 0 ? 2 : 1.5}
                    strokeLinecap="round"
                    opacity={i === 0 ? 0.42 : 0.25}
                    {...draw(0.08 + i * 0.07)}
                />
            ))}
            {notes.map((note, i) => (
                <g key={note.y}>
                    <motion.path
                        d={`M ${note.anchor} ${note.y}
                            C 286 ${note.y}, 304 ${note.y - 10}, 326 ${note.y - 10}
                            L 340 ${note.y - 10}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        opacity="0.3"
                        {...draw(0.38 + i * 0.18)}
                    />
                    <motion.line
                        x1="366"
                        y1={note.y - 18}
                        x2={366 + note.width}
                        y2={note.y - 18}
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        opacity="0.48"
                        {...draw(0.48 + i * 0.18)}
                    />
                    <motion.line
                        x1="366"
                        y1={note.y}
                        x2={366 + note.width * 0.72}
                        y2={note.y}
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        opacity="0.25"
                        {...draw(0.54 + i * 0.18)}
                    />
                </g>
            ))}
            <circle cx="340" cy="54" r="2.5" fill="currentColor" opacity="0.5" />
            <circle cx="340" cy="116" r="2.5" fill="currentColor" opacity="0.5" />
        </svg>
    );
}
